import React, { useEffect, useState } from 'react';
import { EmailMessageDTO } from '../../services/apiClient';
import { PaperclipIcon, SortDesc } from 'lucide-react';
import { useEmailMessageApi } from '../../hooks/useEmailMessageApi';
import { useFileApi } from '../../hooks/useFileApi';

interface EmailListViewerProps {
  componentId: string;
  memberId: string;
  recordId?: string;

}

const EmailListViewer: React.FC<EmailListViewerProps> = ({ componentId, memberId, recordId }) => {
  const [emails, setEmails] = useState<EmailMessageDTO[]>([]);
  const [selectedEmailId, setSelectedEmailId] = useState<string | null>(null);
  const selectedEmail = emails.find((email) => email.id === selectedEmailId);
  const [messageFile, setMessageFile] = useState<string>('');

  const { getFileUrl } = useFileApi();
  const { searchEmailAndThreadMessages: searchEmailAndThreadMessages, isLoading: isLoadingEmails } = useEmailMessageApi();

  useEffect(() => {
    const fetchEmails = async () => {
      try {
        const response = await searchEmailAndThreadMessages({
          filters: {
            componentId,
            memberId,
            ...(recordId && { recordId }),
          },
          page: 1,
          pageSize: 999,
          sortBy: 'CreatedUtc',
          sortDescending: true,
        });
        setEmails(response?.data?.results || []);
      } catch (error) {
        console.error('Error fetching emails');
      }
    };
    fetchEmails();
  }, [recordId]);

  useEffect(() => {
    const fetchMessageFile = async () => {
      if (!selectedEmail?.messageBodyFileId) {
        setMessageFile('');
        return;
      }

      try {
        const response = await getFileUrl(selectedEmail.messageBodyFileId);
        setMessageFile(response.data?.url ?? '');
      } catch (error) {
        console.error('Error fetching message file: ', error);
        setMessageFile('');
      }
    };

    fetchMessageFile();
  }, [selectedEmailId]);

  const formatDate = (date: Date | string | undefined): string => {
    if (!date) return '';

    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const now = new Date();
    const isToday = dateObj.toDateString() === now.toDateString();

    if (isToday) {
      return dateObj.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    }

    return dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const generatePreview = (message: string | undefined): string => {
    if (!message) return '';

    // Remove extra whitespace and newlines, then truncate
    const cleaned = message.replace(/\s+/g, ' ').trim();
    return cleaned.length > 100 ? `${cleaned.substring(0, 100)}...` : cleaned;
  };

  const formatFullDate = (date: Date | string | undefined): string => {
    if (!date) return '';

    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  return (
    <div className="flex h-full border border-gray-200 rounded overflow-hidden bg-white">
      {/* Email List Panel */}
      <div className="w-[350px] border-r border-gray-200 flex flex-col">
        <div className="flex-1 overflow-y-auto">
          {emails.map((email) => (
            <div
              key={email.id}
              className={`p-3 border-b border-gray-100 cursor-pointer transition-colors ${
                selectedEmailId === email.id ? 'bg-blue-50' : 'hover:bg-gray-50'
              }`}
              onClick={() => setSelectedEmailId(email.id || '')}
            >
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm text-gray-900">{email.from}</span>
                <span className="text-xs text-gray-500">{formatDate(email.createdUtc)}</span>
              </div>
              <div className="text-sm mb-1 truncate text-gray-900">{email.subject}</div>
              <div className="text-sm text-gray-600 truncate">{generatePreview(email.message)}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Email Detail Panel */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {selectedEmail ? (
          <>
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-medium mb-4">{selectedEmail.subject}</h2>
              <div className="flex flex-col gap-2">
                <div className="text-sm text-gray-600">
                  <strong className="text-gray-900 mr-2">From:</strong>
                  {selectedEmail.from}
                </div>
                {selectedEmail.replyTo && (
                  <div className="text-sm text-gray-600">
                    <strong className="text-gray-900 mr-2">Reply To:</strong>
                    {selectedEmail.replyTo}
                  </div>
                )}
                {selectedEmail.emailCC && (
                  <div className="text-sm text-gray-600">
                    <strong className="text-gray-900 mr-2">CC:</strong>
                    {selectedEmail.emailCC}
                  </div>
                )}
                <div className="text-sm text-gray-600">
                  {formatFullDate(selectedEmail.createdUtc)}
                </div>
              </div>
            </div>
            <div className="flex-1 p-6 overflow-y-auto text-sm leading-relaxed whitespace-pre-wrap">
              {messageFile ? (
                <iframe src={messageFile} className="w-full h-full border-0"></iframe>
              ) : (
                <div className="text-gray-500">Loading message...</div>
              )}
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-500 text-base">
            <p>Select an email to view its contents</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailListViewer;
